import { useCallback, useEffect, useRef, useState } from 'react';
import { Client as PersonaClient } from 'persona';

/**
 * Reusable Persona inquiry lifecycle hook.
 *
 * Both the user-bound `VerificationPage` (self-serve onboarding) and
 * the session-bound `IdvStepPanel` (clinic site_application) ran the
 * exact same Persona dance:
 *
 *   1. Fetch a "reference" (reference_id, optionally inquiry_id +
 *      session_token, status flags).
 *   2. If the backend says `should_advance`, skip the iframe entirely
 *      and call the caller's `onComplete` with the refreshed payload.
 *   3. Otherwise, expose `open()` so the page can launch the Persona
 *      Client. The hook handles resume vs new inquiry, persists the
 *      inquiry_id on the backend as soon as Persona fires
 *      onEvent('inquiryId'), and de-dupes the final
 *      onComplete/onEvent('complete') confirm call so the backend
 *      isn't double-confirmed.
 *
 * The two pages used to inline ~150 LOC of identical state +
 * callbacks; consolidating here means a Persona behavior change is a
 * one-file edit.
 *
 * Composition: the hook owns everything Persona-specific, callers
 * provide three thin async functions (`fetchReference`,
 * `updateInquiry`, `confirmInquiry`) and one `onComplete` callback.
 * No new class hierarchy — just a plain hook returning a state bag
 * and an `open()` function.
 *
 * Required caller-supplied fns:
 *   fetchReference: () => Promise<{ data, error }>
 *     where data = { reference_id, inquiry_id?, session_token?, status,
 *                    should_advance, application? }
 *   updateInquiry:  ({ reference_id, inquiry_id }) => Promise<...>
 *   confirmInquiry: ({ reference_id, inquiry_id }) => Promise<{ data, error }>
 *
 * onComplete is invoked with whatever the confirm endpoint returns
 * (the verification page uses it to navigate; the clinic page uses
 * it to refresh the Application state).
 *
 * `PersonaCtor` overrides the persona Client constructor for tests.
 *
 * `skip` short-circuits the fetch-on-mount + Persona launcher; useful
 * when the consumer is rendering the panel in a read-only mode (e.g.
 * the clinic runner's revisit view) and must not start a new inquiry.
 */
export const usePersonaInquiry = ({
  fetchReference,
  updateInquiry,
  confirmInquiry,
  onComplete,
  skip = false,
  PersonaCtor = PersonaClient,
  templateId = process.env.REACT_APP_PERSONA_TEMPLATE_ID,
  environmentId = process.env.REACT_APP_PERSONA_ENVIRONMENT,
} = {}) => {
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState(null);

  const personaClientRef = useRef(null);
  const isConfirmingRef = useRef(false);
  const cancelledRef = useRef(false);

  const handleConfirm = useCallback(
    async (referenceId, inquiryId) => {
      if (isConfirmingRef.current) return;
      isConfirmingRef.current = true;
      setWaiting(true);
      try {
        const { data, error: confirmError } = await confirmInquiry({
          reference_id: referenceId,
          inquiry_id: inquiryId,
        });
        if (confirmError) {
          setError(
            confirmError.message || 'Could not confirm verification.',
          );
          return;
        }
        if (onComplete) onComplete(data);
      } finally {
        isConfirmingRef.current = false;
        setWaiting(false);
      }
    },
    [confirmInquiry, onComplete],
  );

  // Fetch the reference on mount; if the backend says we're already
  // done, short-circuit straight to the confirm/advance path.
  // ``skip`` (used by the clinic runner's revisit view) bypasses
  // both the fetch and the Persona launcher entirely so the panel
  // can render in read-only mode without minting a new inquiry.
  useEffect(() => {
    cancelledRef.current = false;
    if (skip) {
      setLoading(false);
      return () => {
        cancelledRef.current = true;
      };
    }
    setLoading(true);
    setError(null);
    fetchReference()
      .then(({ data, error: apiError }) => {
        if (cancelledRef.current) return;
        if (apiError) {
          setError(
            apiError.message || 'Failed to start identity verification.',
          );
          return;
        }
        setReference(data || null);
        if (data?.should_advance && data?.inquiry_id && data?.reference_id) {
          handleConfirm(data.reference_id, data.inquiry_id);
        }
      })
      .finally(() => {
        if (!cancelledRef.current) setLoading(false);
      });
    return () => {
      cancelledRef.current = true;
      if (personaClientRef.current) {
        try {
          personaClientRef.current.destroy();
        } catch (e) {
          // already-destroyed clients throw; harmless
        }
        personaClientRef.current = null;
      }
    };
  }, [skip, fetchReference, handleConfirm]);

  const open = useCallback(() => {
    if (!reference?.reference_id) {
      setError('Verification is not ready yet.');
      return;
    }
    const referenceId = reference.reference_id;
    setOpening(true);
    setError(null);

    const clientConfig = {
      // Resume vs fresh inquiry. Persona handles any status when we
      // pass an inquiryId, including completed/expired sessions; we
      // surface the session_token when we have it for full auth.
      ...(reference.inquiry_id
        ? {
            inquiryId: reference.inquiry_id,
            ...(reference.session_token
              ? { sessionToken: reference.session_token }
              : {}),
          }
        : {
            templateId,
            environmentId,
            referenceId,
          }),
      onComplete: ({ inquiryId }) => {
        handleConfirm(referenceId, inquiryId);
      },
      onCancel: () => {
        setOpening(false);
      },
      onError: (errEvent) => {
        // eslint-disable-next-line no-console
        console.error('Persona inquiry error:', errEvent);
        setError(
          errEvent?.message
            || 'Identity verification encountered an error. Please retry.',
        );
        setOpening(false);
      },
      onEvent: (name, metadata) => {
        // First time we see an inquiryId for a fresh inquiry, persist
        // it server-side so a refresh can resume.
        if (
          metadata?.inquiryId
          && !reference.inquiry_id
          && name !== 'complete'
        ) {
          updateInquiry({
            reference_id: referenceId,
            inquiry_id: metadata.inquiryId,
          }).catch(() => {
            // Best-effort persistence; an outright failure here doesn't
            // block the participant from finishing the flow.
          });
        }
        // Backup completion path — some Persona builds fire
        // onEvent('complete') without onComplete.
        if (name === 'complete' && metadata?.inquiryId) {
          handleConfirm(referenceId, metadata.inquiryId);
        }
      },
    };

    const client = new PersonaCtor(clientConfig);
    personaClientRef.current = client;
    client.open();
  }, [reference, handleConfirm, updateInquiry, PersonaCtor, templateId, environmentId]);

  return {
    reference,
    status: reference?.status || null,
    isVerified:
      reference?.status === 'COMPLETED' || reference?.status === 'APPROVED',
    loading,
    opening,
    waiting,
    error,
    setError,
    open,
  };
};

export default usePersonaInquiry;
